import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import HeroSlider from '../components/HeroSlider';
import CategoryMenuCarousel from '../components/CategoryMenuCarousel';
import CategoryGrid from '../components/CategoryGrid';
import ProductCard from '../components/ProductCard';
import BannerSection from '../components/BannerSection';
import PromoCategoryCards from '../components/PromoCategoryCards';
import DiscountProductsCarousel from '../components/DiscountProductsCarousel';
import OtherProductsCarousel from '../components/OtherProductsCarousel';
import BestSellersSection from '../components/BestSellersSection';
import FreshFindsSection from '../components/FreshFindsSection';
import InfoBar from '../components/InfoBar';
import FeaturesSection from '../components/FeaturesSection';
import BrandsSection from '../components/BrandsSection';
import CountdownTimer from '../components/CountdownTimer';
import Preloader from '../components/Preloader';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SEO, { SITE_URL, SITE_NAME } from '../components/SEO';
import { cachedGet, getCached, primeCache } from '../lib/apiCache';
import 'swiper/css';
import 'swiper/css/navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  // Hydrate from cache so back/forward navigation is instant.
  const [products, setProducts] = useState(getCached('/api/products?limit=100') || []);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [settings, setSettings] = useState(getCached('/api/settings') || null);

  // 1.5s preloader, but only once per browser session — shown on the very
  // first HomePage visit, then skipped on subsequent visits.
  const [showPreloader, setShowPreloader] = useState(() => {
    try {
      return !sessionStorage.getItem('agrosmart_home_loaded');
    } catch (e) {
      return true;
    }
  });

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    if (!showPreloader) return;
    const timer = setTimeout(() => {
      setShowPreloader(false);
      try {
        sessionStorage.setItem('agrosmart_home_loaded', '1');
      } catch (e) {
        /* ignore */
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [showPreloader]);

  useEffect(() => {
    loadHomeData();
  }, []);

  useEffect(() => {
    if (settings?.featuredCategoryId) {
      fetchFeaturedProducts(settings.featuredCategoryId);
    }
  }, [settings]);

  const loadHomeData = async () => {
    try {
      // Warm up ALL endpoints that sub-components (HeroSlider, BrandsSection,
      // BannerSection, BestSellersSection, CategoryMenuCarousel etc.) need.
      // They use the same cachedGet keys, so concurrent calls de-dupe and
      // resolve instantly once the data lands here.
      const [settingsData, productsData, categoriesData, brandsData] =
        await Promise.all([
          cachedGet('/api/settings').catch(() => null),
          cachedGet('/api/products?limit=100').catch(() => []),
          cachedGet('/api/categories').catch(() => []),
          cachedGet('/api/brands').catch(() => []),
        ]);

      setSettings(settingsData);
      setProducts(productsData || []);

      // Prime per-product cache so clicking from list opens detail instantly.
      (productsData || []).forEach((p) => {
        if (p?.id) primeCache(`/api/products/${p.slug || p.id}`, p);
      });

      // Avoid unused-var lint
      void categoriesData;
      void brandsData;
    } catch (error) {
      console.error('Error loading homepage data:', error);
    }
  };

  const fetchFeaturedProducts = async (featuredCategoryId) => {
    try {
      const categories = (await cachedGet('/api/categories')) || [];
      const category = categories.find((cat) => cat.id === featuredCategoryId);

      if (category) {
        const data = await cachedGet(
          `/api/products?category=${encodeURIComponent(category.name)}&limit=20`
        );
        setFeaturedProducts(data || []);
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  // Product sections
  const hotPicksProducts = products.slice(0, 6);
  const flashDealProducts = products.slice(6, 11);
  const freshFindsProducts = products.slice(11, 19);

  return (
    <div className="relative min-h-screen">
      <SEO
        title=""
        description="AgroSmart.md — magazin online de utilaje agricole, motocultoare, semințe, îngrășăminte, irigare, unelte de grădină și sere în Moldova. Livrare rapidă, achitare în 3 rate fără dobândă."
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: SITE_NAME,
            url: SITE_URL,
            logo: `${SITE_URL}/favicon.png`,
            sameAs: [],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: SITE_URL,
            name: SITE_NAME,
            potentialAction: {
              '@type': 'SearchAction',
              target: `${SITE_URL}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          },
        ]}
      />

      {showPreloader && <Preloader />}

      {/* Bara cu categorii din meniu */}
      <CategoryMenuCarousel />

      {/* Hero Slider */}
      <HeroSlider />

      {/* Branduri carousel */}
      <BrandsSection />

      {/* Lista categorii */}
      {/* <CategoryGrid /> */}

      {/* Taburi cu produse */}
      <BestSellersSection />

      {/* Cele 4 categorii */}
      <PromoCategoryCards />

      {/* Carousel cu produse */}
      <DiscountProductsCarousel />

      {/* Timer */}
      <CountdownTimer targetDate="2026-07-15T23:59:59+03:00" />

      {/* Bannere */}
      <BannerSection />

      {/* Bara informativa */}
      {/* <InfoBar /> */}

      {/* Taburi cu produse */}
      {/* <FreshFindsSection /> */}

      {/* Al doilea carousel cu produse — deasupra footerului */}
      <OtherProductsCarousel />

      {/* Features Section */}
      {/* <FeaturesSection /> */}
    </div>
  );
};

export default HomePage;